import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    simulateGatewayPassFormSchema,
    SimulateGatewayPassFormSchemaValues,
    useSimulateGatewayPass,
} from 'src/features/actions/api/useSimulateGatewayPass';
import SimulateLPRDetectionForm from 'src/features/actions/components/form/SimulateLPRDetectionForm';
import { actionsMessages } from 'src/features/actions/utils/messages';
import { toast } from 'src/hooks/UseToast';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useParking } from 'src/providers/ParkingProvider';
import { eventMessages } from 'src/features/eventLog/utils/messages';
import { gatewayPassNotAllowedReasonTranslationMap } from 'src/features/eventLog/utils/constants';

type CreateLPRDetectionFormModalProps = {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
};
const SimulateLPRDetectionFormModal = ({ isModalOpen, setIsModalOpen }: CreateLPRDetectionFormModalProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const form = useForm<SimulateGatewayPassFormSchemaValues>({
        resolver: zodResolver(simulateGatewayPassFormSchema),
        defaultValues: {},
    });

    const onError = useOnError();

    const { mutate, isPending } = useSimulateGatewayPass({
        onSuccess: (success) => {
            setIsModalOpen(false);

            const isGatewayPassSuccessful = success.gateway_pass_successful;
            const gatewayPassNotAllowedReason = success.reason
                ? formatMessage(
                      gatewayPassNotAllowedReasonTranslationMap[success.reason] ?? {
                          id: 'common.unknown',
                          defaultMessage: success.reason,
                      },
                  )
                : '';

            const toastDescription = isGatewayPassSuccessful
                ? formatMessage(eventMessages.gatewayPassAllowed)
                : `${formatMessage(eventMessages.gatewayPassNotAllowed)}. ${gatewayPassNotAllowedReason}`;

            toast({
                title: formatMessage(actionsMessages.createdLPRdetection),
                description: toastDescription,
            });
        },
        onError: (error) => {
            onError(error, form.setError);
        },
    });

    const onSubmit: SubmitHandler<SimulateGatewayPassFormSchemaValues> = (values) => {
        const formData = new FormData();

        formData.append('gateway', values.gateway);
        formData.append('identifier_type', values.identifier_type);
        if (values.identifier_value) {
            formData.append('identifier_value', values.identifier_value);
        }
        if (values.files) {
            values.files.forEach((file) => {
                formData.append('files', file);
            });
        }

        mutate({ formData, parkingId });
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
        if (isOpen) {
            form.reset();
        }
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogContent>
                <DialogHeader>
                    <div className="flex gap-4">
                        <SquareIcon icon={ICON_MAP.CAMERA} />
                        <div>
                            <DialogTitle>{formatMessage(actionsMessages.lprDetection)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(actionsMessages.lprDetectionDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    <SimulateLPRDetectionForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button variant="default" type="submit" onClick={form.handleSubmit(onSubmit)} className="flex-1">
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(actionsMessages.createLprDetection)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SimulateLPRDetectionFormModal;
